import React, { useEffect, useState } from 'react';
import { Sidebar } from './components/Sidebar';
import { LandingPage } from './components/LandingPage';
import { HomePage } from './components/HomePage';
import { StoriesPage } from './components/StoriesPage';
import { CreatePage } from './components/CreatePage';
import { CreationsPage } from './components/CreationsPage';
import { StoryDetailPage } from './components/StoryDetailPage';
import { StorybookDetailPage } from './components/StorybookDetailPage';
import { StorybookViewer } from './components/StorybookViewer';
import { RecordingScreen } from './components/RecordingScreen';
import { SessionCompleteScreen } from './components/SessionCompleteScreen';
import { StorybookSetupPage, BookConfiguration } from './components/StorybookSetupPage';
import { ReviewConfirmPage } from './components/ReviewConfirmPage';
import { PromptsLibrary } from './components/PromptsLibrary';
import { FamilyPage } from './components/FamilyPage';
import { ReminiscePage } from './components/ReminiscePage';
import { LabsPage } from './components/LabsPage';
import { Toast, ToastType } from './components/Toast';
import { KeyboardShortcutsGuide } from './components/KeyboardShortcutsGuide';
import { QuickActionButton } from './components/QuickActionButton';
import { OnboardingTour } from './components/OnboardingTour';
import { InteractiveTour } from './components/InteractiveTour';
import { TourButton } from './components/TourButton';
import { StorybookGenerationProgress } from './components/StorybookGenerationProgress';
import { GlobalSearch } from './components/GlobalSearch';
import { SuccessConfetti } from './components/SuccessConfetti';
import { HomeDesignTester } from './components/HomeDesignTester';
type RecordingFlow = 'none' | 'recording' | 'complete';
type StorybookFlow = 'none' | 'setup' | 'review' | 'generating';
type GenerationStage = 'analyzing' | 'generating' | 'illustrating' | 'finalizing' | 'complete';
type ToastMessage = {
  id: number;
  message: string;
  type: ToastType;
};
export function App() {
  const [activePage, setActivePage] = useState('landing');
  const [showLanding, setShowLanding] = useState(true);
  const [recordingFlow, setRecordingFlow] = useState<RecordingFlow>('none');
  const [storybookFlow, setStorybookFlow] = useState<StorybookFlow>('none');
  const [selectedStoryId, setSelectedStoryId] = useState<number | null>(null);
  const [selectedStorybookId, setSelectedStorybookId] = useState<number | null>(null);
  const [bookConfiguration, setBookConfiguration] = useState<BookConfiguration | null>(null);
  const [isViewingStorybook, setIsViewingStorybook] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [toastIdCounter, setToastIdCounter] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showInteractiveTour, setShowInteractiveTour] = useState(false);
  const [generationStage, setGenerationStage] = useState<GenerationStage>('analyzing');
  const [generationProgress, setGenerationProgress] = useState({
    currentPage: 0,
    totalPages: 8
  });
  const [newlyCreatedStoryId, setNewlyCreatedStoryId] = useState<number | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
    const hasSeenLanding = localStorage.getItem('hasSeenLanding');
    if (!hasSeenLanding) {
      setShowLanding(true);
      setActivePage('landing');
    } else if (!hasSeenOnboarding) {
      setShowLanding(false);
      setShowOnboarding(true);
      setActivePage('home');
    } else {
      setShowLanding(false);
      setActivePage('home');
    }
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '?' && !e.shiftKey) {
        e.preventDefault();
        setShowKeyboardShortcuts(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowGlobalSearch(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const showToast = (message: string, type: ToastType = 'success') => {
    const id = toastIdCounter;
    setToastIdCounter(prev => prev + 1);
    setToasts(prev => [...prev, {
      id,
      message,
      type
    }]);
    if (type === 'success' && message.includes('ready')) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 100);
    }
  };
  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };
  const handleGetStarted = () => {
    setShowLanding(false);
    localStorage.setItem('hasSeenLanding', 'true');
    setShowOnboarding(true);
    setActivePage('home');
  };
  const handleTryDemo = () => {
    setShowLanding(false);
    localStorage.setItem('hasSeenLanding', 'true');
    setShowOnboarding(true);
    setActivePage('home');
  };
  const handleCompleteOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem('hasSeenOnboarding', 'true');
    setShowInteractiveTour(false);
  };
  const handleSkipOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem('hasSeenOnboarding', 'true');
    setShowInteractiveTour(false);
  };
  const handleStartInteractiveTour = () => {
    setShowOnboarding(false);
    setShowInteractiveTour(true);
    setActivePage('stories');
  };
  const handleRestartTour = () => {
    setShowOnboarding(true);
  };
  const handleCompleteInteractiveTour = () => {
    setShowInteractiveTour(false);
  };
  const handleSkipInteractiveTour = () => {
    setShowInteractiveTour(false);
  };
  const handleStartRecording = () => {
    setRecordingFlow('recording');
  };
  const handleEndRecording = () => {
    const newStoryId = Date.now();
    setNewlyCreatedStoryId(newStoryId);
    setRecordingFlow('complete');
    showToast('Recording saved successfully!', 'success');
  };
  const handleBackToStories = () => {
    setRecordingFlow('none');
    setNewlyCreatedStoryId(null);
    setActivePage('stories');
  };
  const handleViewNewStory = () => {
    if (newlyCreatedStoryId) {
      setSelectedStoryId(newlyCreatedStoryId);
      setRecordingFlow('none');
      setActivePage('story-detail');
    }
  };
  const handleViewStory = (storyId: number) => {
    setSelectedStoryId(storyId);
    setActivePage('story-detail');
  };
  const handleBackFromStoryDetail = () => {
    setSelectedStoryId(null);
    setActivePage('stories');
  };
  const handleStartStorybookCreation = (storyId: number) => {
    setSelectedStoryId(storyId);
    setStorybookFlow('setup');
  };
  const handleStorybookSetupContinue = (config: BookConfiguration) => {
    setBookConfiguration(config);
    setStorybookFlow('review');
  };
  const handleStorybookReviewBack = () => {
    setStorybookFlow('setup');
  };
  const handleStorybookCreate = () => {
    setStorybookFlow('generating');
    let currentPage = 0;
    const totalPages = 8;
    const stages: GenerationStage[] = ['analyzing', 'generating', 'illustrating', 'finalizing', 'complete'];
    let stageIndex = 0;
    const interval = setInterval(() => {
      if (stageIndex < stages.length - 1) {
        setGenerationStage(stages[stageIndex]);
        currentPage = Math.min(currentPage + 1, totalPages);
        setGenerationProgress({
          currentPage,
          totalPages
        });
        if (currentPage >= totalPages) {
          stageIndex++;
        }
      } else {
        setGenerationStage('complete');
        clearInterval(interval);
        setTimeout(() => {
          setStorybookFlow('none');
          setSelectedStoryId(null);
          setBookConfiguration(null);
          setActivePage('creations');
          showToast('Your storybook is ready!', 'success');
        }, 2000);
      }
    }, 1500);
  };
  const handleBackFromStorybookSetup = () => {
    setStorybookFlow('none');
    setSelectedStoryId(null);
    setActivePage('stories');
  };
  const handleViewStorybook = (storybookId: number) => {
    setSelectedStorybookId(storybookId);
    setActivePage('storybook-detail');
  };
  const handleBackFromStorybookDetail = () => {
    setSelectedStorybookId(null);
    setActivePage('creations');
  };
  const handleReadStorybook = () => {
    setIsViewingStorybook(true);
  };
  const handleCloseStorybookViewer = () => {
    setIsViewingStorybook(false);
  };
  const handleQuickRecordStory = () => {
    setActivePage('stories');
    setTimeout(() => {
      handleStartRecording();
    }, 100);
  };
  const handleSelectPrompt = (prompt: string) => {
    showToast(`Prompt selected: "${prompt}"`, 'info');
    setActivePage('stories');
    setTimeout(() => {
      handleStartRecording();
    }, 100);
  };
  const handleNavigate = (page: string) => {
    if (page !== activePage) {
      setRecordingFlow('none');
      setStorybookFlow('none');
      setIsViewingStorybook(false);
    }
    setActivePage(page);
  };
  const handleSearchResult = (result: any) => {
    if (result.type === 'story') {
      handleViewStory(result.id);
    } else if (result.type === 'storybook') {
      handleViewStorybook(result.id);
    }
  };
  // Show landing page
  if (showLanding) {
    return <LandingPage onGetStarted={handleGetStarted} onTryDemo={handleTryDemo} data-id="element-0" />;
  }
  if (recordingFlow === 'recording') {
    return <RecordingScreen onEnd={handleEndRecording} data-id="element-1" />;
  }
  if (recordingFlow === 'complete' && newlyCreatedStoryId) {
    return <div className="flex min-h-screen w-full" data-id="element-2">
        <Sidebar activePage="stories" onNavigate={handleNavigate} onOpenSearch={() => setShowGlobalSearch(true)} isMinimized={isSidebarMinimized} onToggleMinimize={() => setIsSidebarMinimized(!isSidebarMinimized)} data-id="element-3" />
        <div className={`flex-1 transition-all duration-300 ${isSidebarMinimized ? 'ml-20' : 'ml-64'}`} data-id="element-4">
          <SessionCompleteScreen onBackToStories={handleBackToStories} onViewStory={handleViewNewStory} storyId={newlyCreatedStoryId} duration="2:34" title="My New Story" data-id="element-5" />
        </div>
      </div>;
  }
  if (storybookFlow === 'setup' && selectedStoryId) {
    return <div className="flex min-h-screen w-full" data-id="element-6">
        <Sidebar activePage="create" onNavigate={handleNavigate} onOpenSearch={() => setShowGlobalSearch(true)} isMinimized={isSidebarMinimized} onToggleMinimize={() => setIsSidebarMinimized(!isSidebarMinimized)} data-id="element-7" />
        <div className={`flex-1 transition-all duration-300 ${isSidebarMinimized ? 'ml-20' : 'ml-64'}`} data-id="element-8">
          <StorybookSetupPage storyId={selectedStoryId} onBack={handleBackFromStorybookSetup} onContinue={handleStorybookSetupContinue} data-id="element-9" />
        </div>
      </div>;
  }
  if (storybookFlow === 'review' && selectedStoryId && bookConfiguration) {
    return <div className="flex min-h-screen w-full" data-id="element-10">
        <Sidebar activePage="create" onNavigate={handleNavigate} onOpenSearch={() => setShowGlobalSearch(true)} isMinimized={isSidebarMinimized} onToggleMinimize={() => setIsSidebarMinimized(!isSidebarMinimized)} data-id="element-11" />
        <div className={`flex-1 transition-all duration-300 ${isSidebarMinimized ? 'ml-20' : 'ml-64'}`} data-id="element-12">
          <ReviewConfirmPage storyId={selectedStoryId} config={bookConfiguration} onBack={handleStorybookReviewBack} onCreate={handleStorybookCreate} data-id="element-13" />
        </div>
      </div>;
  }
  if (storybookFlow === 'generating') {
    return <>
        <div className="flex min-h-screen w-full" data-id="element-14">
          <Sidebar activePage="create" onNavigate={handleNavigate} onOpenSearch={() => setShowGlobalSearch(true)} isMinimized={isSidebarMinimized} onToggleMinimize={() => setIsSidebarMinimized(!isSidebarMinimized)} data-id="element-15" />
          <div className={`flex-1 transition-all duration-300 ${isSidebarMinimized ? 'ml-20' : 'ml-64'}`} data-id="element-16" />
        </div>
        <StorybookGenerationProgress stage={generationStage} currentPage={generationProgress.currentPage} totalPages={generationProgress.totalPages} estimatedTimeRemaining={120} data-id="element-17" />
      </>;
  }
  if (isViewingStorybook && selectedStorybookId) {
    return <>
        <div className="flex min-h-screen w-full" data-id="element-18">
          <Sidebar activePage={activePage} onNavigate={handleNavigate} onOpenSearch={() => setShowGlobalSearch(true)} isMinimized={isSidebarMinimized} onToggleMinimize={() => setIsSidebarMinimized(!isSidebarMinimized)} data-id="element-19" />
          <div className={`flex-1 transition-all duration-300 ${isSidebarMinimized ? 'ml-20' : 'ml-64'}`} data-id="element-20">
            {activePage === 'storybook-detail' && selectedStorybookId && <StorybookDetailPage storybookId={selectedStorybookId} onBack={handleBackFromStorybookDetail} onReadStorybook={handleReadStorybook} data-id="element-21" />}
          </div>
        </div>
        <StorybookViewer storybookId={selectedStorybookId} onClose={handleCloseStorybookViewer} data-id="element-22" />
      </>;
  }
  const renderPage = () => {
    switch (activePage) {
      case 'home':
        return <HomePage onNavigate={handleNavigate} onViewStory={handleViewStory} data-id="element-23" />;
      case 'stories':
        return <StoriesPage onStartRecording={handleStartRecording} onViewStory={handleViewStory} data-id="element-24" />;
      case 'create':
        return <CreatePage onStartStorybookCreation={handleStartStorybookCreation} data-id="element-25" />;
      case 'creations':
        return <CreationsPage onViewStorybook={handleViewStorybook} data-id="element-26" />;
      case 'design-tester':
        return <HomeDesignTester onNavigate={handleNavigate} onViewStory={handleViewStory} onStartStorybookCreation={handleStartStorybookCreation} onStartRecording={handleStartRecording} data-id="element-27" />;
      case 'prompts':
        return <PromptsLibrary onSelectPrompt={handleSelectPrompt} data-id="element-28" />;
      case 'family':
        return <FamilyPage data-id="element-29" />;
      case 'reminisce':
        return <ReminiscePage data-id="element-30" />;
      case 'labs':
        return <LabsPage data-id="element-31" />;
      case 'story-detail':
        return selectedStoryId ? <StoryDetailPage storyId={selectedStoryId} onBack={handleBackFromStoryDetail} onShowToast={showToast} onCreateStorybook={handleStartStorybookCreation} data-id="element-32" /> : <StoriesPage onStartRecording={handleStartRecording} onViewStory={handleViewStory} data-id="element-33" />;
      case 'storybook-detail':
        return selectedStorybookId ? <StorybookDetailPage storybookId={selectedStorybookId} onBack={handleBackFromStorybookDetail} onReadStorybook={handleReadStorybook} data-id="element-34" /> : <CreationsPage onViewStorybook={handleViewStorybook} data-id="element-35" />;
      default:
        return <HomePage onNavigate={handleNavigate} onViewStory={handleViewStory} data-id="element-36" />;
    }
  };
  return <>
      <div className="flex min-h-screen w-full" data-id="element-37">
        <Sidebar activePage={activePage} onNavigate={handleNavigate} onOpenSearch={() => setShowGlobalSearch(true)} isMinimized={isSidebarMinimized} onToggleMinimize={() => setIsSidebarMinimized(!isSidebarMinimized)} data-id="element-38" />
        <div className={`flex-1 transition-all duration-300 ${isSidebarMinimized ? 'ml-20' : 'ml-64'}`} data-id="element-39">
          {renderPage()}
        </div>
      </div>

      {/* Tour Button - Always visible */}
      {!isViewingStorybook && recordingFlow === 'none' && storybookFlow === 'none' && !showInteractiveTour && <TourButton onStartTour={handleRestartTour} data-id="element-40" />}

      {/* Interactive Tour */}
      <InteractiveTour isActive={showInteractiveTour} onComplete={handleCompleteInteractiveTour} onSkip={handleSkipInteractiveTour} currentPage={activePage} data-id="element-41" />

      {toasts.map(toast => <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} data-id="element-42" />)}

      <KeyboardShortcutsGuide isOpen={showKeyboardShortcuts} onClose={() => setShowKeyboardShortcuts(false)} data-id="element-43" />

      <GlobalSearch isOpen={showGlobalSearch} onClose={() => setShowGlobalSearch(false)} onSelectResult={handleSearchResult} data-id="element-44" />

      <OnboardingTour isOpen={showOnboarding} onComplete={handleCompleteOnboarding} onSkip={handleSkipOnboarding} onNavigate={handleNavigate} onStartInteractiveTour={handleStartInteractiveTour} data-id="element-45" />

      <SuccessConfetti trigger={showConfetti} data-id="element-46" />
    </>;
}